import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, LogOut, RefreshCw, Trash2, Mail, Calendar } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'nts_admin_token';

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [filter, setFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [active, setActive] = useState(null);

  const authedClient = (t = token) => axios.create({
    baseURL: API,
    headers: { 'X-Admin-Token': t },
  });

  const load = async (t = token) => {
    setBusy(true);
    try {
      const { data } = await authedClient(t).get('/admin/submissions');
      setSubmissions(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken('');
        toast.error('Session expired. Please sign in.');
      } else { toast.error('Failed to load submissions'); }
    } finally { setBusy(false); }
  };

  useEffect(() => { if (token) load(token); }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword('');
      toast.success('Welcome.');
    } catch {
      toast.error('Incorrect password.');
    } finally { setBusy(false); }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    setSubmissions([]);
    setActive(null);
  };

  const updateStatus = async (id, status) => {
    try {
      await authedClient().patch(`/admin/submissions/${id}`, { status });
      setSubmissions((s) => s.map((x) => x.id === id ? { ...x, status } : x));
      if (active?.id === id) setActive({ ...active, status });
      toast.success('Updated.');
    } catch { toast.error('Update failed'); }
  };

  const remove = async (id) => {
    if (!confirm('Permanently delete this submission? This cannot be undone.')) return;
    try {
      await authedClient().delete(`/admin/submissions/${id}`);
      setSubmissions((s) => s.filter((x) => x.id !== id));
      if (active?.id === id) setActive(null);
      toast.success('Deleted.');
    } catch { toast.error('Delete failed'); }
  };

  const filtered = submissions.filter((s) => {
    if (filter !== 'all' && s.kind !== filter) return false;
    if (dateFrom) {
      const fromTs = new Date(dateFrom + 'T00:00:00').getTime();
      if (new Date(s.created_at).getTime() < fromTs) return false;
    }
    if (dateTo) {
      const toTs = new Date(dateTo + 'T23:59:59').getTime();
      if (new Date(s.created_at).getTime() > toTs) return false;
    }
    return true;
  });

  if (!token) {
    return (
      <div data-testid="admin-login" className="min-h-[80vh] flex items-center justify-center px-6">
        <div className="bg-brand-bone rounded-3xl p-10 max-w-md w-full border border-brand-brown/5">
          <div className="h-12 w-12 rounded-full bg-brand-sage flex items-center justify-center mb-6">
            <Lock size={18} className="text-brand-brown" />
          </div>
          <h1 className="font-serif text-3xl text-brand-brown">Admin</h1>
          <p className="mt-2 font-sans text-sm text-brand-brown-light/90">
            For practitioner use only. Information shown here is held with care and confidentiality.
          </p>
          <form onSubmit={handleLogin} className="mt-8 space-y-5">
            <div>
              <label className="block font-sans text-[12px] uppercase tracking-[0.22em] text-brand-brown-light/90 mb-2">Password</label>
              <input
                type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password-input"
                className="w-full bg-brand-bg-soft border border-brand-brown/10 text-brand-brown px-4 py-3 rounded-2xl font-sans focus:outline-none focus:border-brand-gold"
              />
            </div>
            <button
              type="submit" disabled={busy} data-testid="admin-login-submit"
              className="font-sans text-sm tracking-wide bg-brand-brown text-brand-bg rounded-full px-7 py-3 hover:bg-brand-brown-light transition-colors duration-500 disabled:opacity-60"
            >
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="max-w-7xl mx-auto px-6 md:px-12 py-16">
      <div className="flex items-center justify-between mb-10">
        <div>
          <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-brown-muted mb-2">Admin</p>
          <h1 className="font-serif text-4xl text-brand-brown">Submissions</h1>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => load()} data-testid="admin-refresh" className="inline-flex items-center gap-2 font-sans text-sm tracking-wide border border-brand-brown/20 text-brand-brown rounded-full px-5 py-2.5 hover:bg-brand-bone">
            <RefreshCw size={14} /> Refresh
          </button>
          <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 font-sans text-sm tracking-wide border border-brand-brown/20 text-brand-brown rounded-full px-5 py-2.5 hover:bg-brand-bone">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </div>

      <div className="mb-6 flex flex-wrap items-end gap-4">
        <div className="inline-flex bg-brand-bone rounded-full p-1.5 border border-brand-brown/5 flex-wrap">
          {['all', 'application', 'intake', 'subscriber'].map((f) => (
            <button
              key={f}
              data-testid={`admin-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`font-sans text-sm tracking-wide px-5 py-2 rounded-full transition-colors duration-300 capitalize ${filter === f ? 'bg-brand-brown text-brand-bg' : 'text-brand-brown-light'}`}
            >
              {f === 'all' ? 'All' : f === 'application' ? 'Applications' : f === 'intake' ? 'Intakes' : 'Subscribers'} {f === filter ? `(${filtered.length})` : ''}
            </button>
          ))}
        </div>

        <div className="flex items-end gap-3 bg-brand-bone rounded-2xl p-3 border border-brand-brown/5">
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.22em] text-brand-brown-muted mb-1">From</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              data-testid="admin-date-from"
              className="bg-brand-bg-soft border border-brand-brown/10 text-brand-brown rounded-full px-3 py-1.5 font-sans text-sm focus:outline-none focus:border-brand-gold"
            />
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.22em] text-brand-brown-muted mb-1">To</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              data-testid="admin-date-to"
              className="bg-brand-bg-soft border border-brand-brown/10 text-brand-brown rounded-full px-3 py-1.5 font-sans text-sm focus:outline-none focus:border-brand-gold"
            />
          </label>
          {(dateFrom || dateTo) && (
            <button
              onClick={() => { setDateFrom(''); setDateTo(''); }}
              data-testid="admin-date-clear"
              className="font-sans text-xs text-brand-brown-muted hover:text-brand-brown px-2 py-1.5"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_1.4fr] gap-6">
        {/* LIST */}
        <div className="bg-brand-bone rounded-3xl border border-brand-brown/5 overflow-hidden">
          {busy && submissions.length === 0 ? (
            <div className="p-10 text-center font-sans text-sm text-brand-brown-muted">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center font-sans text-sm text-brand-brown-muted">No submissions yet.</div>
          ) : (
            <ul className="divide-y divide-brand-brown/5 max-h-[70vh] overflow-y-auto">
              {filtered.map((s) => (
                <li key={s.id}>
                  <button
                    onClick={() => setActive(s)}
                    data-testid={`admin-row-${s.id}`}
                    className={`w-full text-left p-5 hover:bg-brand-bg-soft transition-colors ${active?.id === s.id ? 'bg-brand-bg-soft' : ''}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-serif text-lg text-brand-brown truncate">{s.full_name || s.email}</div>
                        <div className="font-sans text-xs text-brand-brown-muted flex items-center gap-3 mt-1">
                          {s.full_name && <span className="inline-flex items-center gap-1"><Mail size={11} /> {s.email}</span>}
                          <span className="inline-flex items-center gap-1"><Calendar size={11} /> {new Date(s.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <StatusBadge status={s.status} kind={s.kind} />
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* DETAIL */}
        <div className="bg-brand-bone rounded-3xl p-8 md:p-10 border border-brand-brown/5">
          {!active ? (
            <div className="text-center py-20 font-sans text-sm text-brand-brown-muted">
              Select a submission to view details.
            </div>
          ) : (
            <div data-testid="admin-detail">
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-gold mb-2">
                    {active.kind === 'application' ? 'Application' : active.kind === 'intake' ? 'Intake request' : 'Newsletter subscriber'}
                  </p>
                  <h2 className="font-serif text-3xl text-brand-brown">{active.full_name || active.email}</h2>
                  <p className="font-sans text-sm text-brand-brown-light mt-1">
                    <a href={`mailto:${active.email}`} className="hover:underline">{active.email}</a>
                    {active.pronouns && <span className="text-brand-brown-muted"> · {active.pronouns}</span>}
                    {active.country_timezone && <span className="text-brand-brown-muted"> · {active.country_timezone}</span>}
                    {active.source && <span className="text-brand-brown-muted"> · source: {active.source}</span>}
                  </p>
                  <p className="font-sans text-xs text-brand-brown-muted mt-1">{new Date(active.created_at).toLocaleString()}</p>
                </div>
                <StatusBadge status={active.status} kind={active.kind} />
              </div>

              <div className="flex flex-wrap gap-2 mb-6">
                {active.kind !== 'subscriber' && ['new', 'reviewed', 'invited', 'declined'].map((st) => (
                  <button
                    key={st}
                    onClick={() => updateStatus(active.id, st)}
                    data-testid={`admin-status-${st}`}
                    className={`font-sans text-xs uppercase tracking-[0.18em] px-3.5 py-1.5 rounded-full border transition-colors ${active.status === st ? 'bg-brand-brown text-brand-bg border-brand-brown' : 'border-brand-brown/20 text-brand-brown hover:bg-brand-bg-soft'}`}
                  >
                    {st}
                  </button>
                ))}
                <button onClick={() => remove(active.id)} data-testid="admin-delete" className="ml-auto inline-flex items-center gap-1.5 font-sans text-xs uppercase tracking-[0.18em] px-3.5 py-1.5 rounded-full border border-red-200 text-red-700 hover:bg-red-50">
                  <Trash2 size={12} /> Delete
                </button>
              </div>

              {active.kind === 'application' ? (
                <div className="space-y-5">
                  {active.path_interest?.length > 0 && (
                    <Section label="Paths of interest">
                      <div className="flex flex-wrap gap-2">
                        {active.path_interest.map((p) => (
                          <span key={p} className="font-sans text-xs px-3 py-1.5 rounded-full bg-brand-sage text-brand-brown">{p}</span>
                        ))}
                      </div>
                    </Section>
                  )}
                  {active.preferred_start && <Section label="Preferred start">{active.preferred_start}</Section>}
                  {active.drawn_to_programme && <Section label="What has drawn you to this programme?"><p className="whitespace-pre-line">{active.drawn_to_programme}</p></Section>}
                  {active.current_relationship && <Section label="Current relationship with food, body & self"><p className="whitespace-pre-line">{active.current_relationship}</p></Section>}
                  {active.hopes && <Section label="Hopes from this journey"><p className="whitespace-pre-line">{active.hopes}</p></Section>}
                  <div className="grid sm:grid-cols-2 gap-4">
                    {active.prior_therapy && <Section label="Prior therapy / ED support">{active.prior_therapy}</Section>}
                    {active.current_support && <Section label="Currently receiving support">{active.current_support}</Section>}
                  </div>
                  {active.additional_notes && <Section label="Additional notes"><p className="whitespace-pre-line">{active.additional_notes}</p></Section>}
                </div>
              ) : active.kind === 'intake' ? (
                <div className="space-y-5">
                  {active.message ? (
                    <Section label="Message"><p className="whitespace-pre-line">{active.message}</p></Section>
                  ) : (
                    <p className="font-sans text-sm text-brand-brown-muted italic">No additional message provided.</p>
                  )}
                </div>
              ) : (
                <div className="space-y-5">
                  <p className="font-sans text-sm text-brand-brown-light/90 leading-relaxed">
                    This person has asked to be kept gently informed. Reach out to them when there is something meaningful to share — new Path dates, retreat announcements, talks.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <p className="mt-12 font-sans text-xs text-brand-brown-muted italic text-center max-w-2xl mx-auto">
        Held with confidentiality. Treat all information here as you would a clinical record — in line with GDPR principles.
      </p>
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div>
      <p className="font-sans text-[11px] uppercase tracking-[0.22em] text-brand-brown-muted mb-2">{label}</p>
      <div className="font-sans text-base text-brand-brown-light/95 leading-relaxed">{children}</div>
    </div>
  );
}

function StatusBadge({ status, kind }) {
  const colors = {
    new:        'bg-brand-peach text-brand-brown',
    reviewed:   'bg-brand-sage text-brand-brown',
    invited:    'bg-brand-brown text-brand-bg',
    declined:   'bg-stone-200 text-stone-700',
    subscribed: 'bg-brand-sage text-brand-brown',
  };
  return (
    <div className="flex flex-col items-end gap-1">
      <span className={`font-sans text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 rounded-full ${colors[status] || colors.new}`}>{status}</span>
      <span className="font-sans text-[10px] uppercase tracking-[0.22em] text-brand-brown-muted">{kind}</span>
    </div>
  );
}
